<script>
import {
  mapActions, mapState, mapGetters, mapMutations,
} from 'vuex';
import { orderBy, uniqBy, isEmpty } from 'lodash';
import api from 'src/api';
import { validateEmail } from 'src/utils/helper';
import { C_ATTACHMENT_MAX_SIZE, C_ATTACHMENT_MAX_COUNT } from 'src/store/modules/app';
import { BACKEND_HTTP_PATH } from 'src/config';
import { toast, consts } from 'src/utils';

export default {
  props: ['value'],
  data() {
    return {
      header: this.value.header,
      requestTypeId: null,
      errorNotification: false,
      message: '',
      disabled: false,
      emails: [],
      errors: {
        message: '',
        email: '',
        validate: '',
      },
      isSend: false,
    };
  },
  filters: {
    fullpath(path) {
      return path.replace(/^\//, BACKEND_HTTP_PATH);
    },
  },
  created() {
    this.fetchDepartments();
    api.clearUploads(this.value.activityId, true);
  },
  beforeDestroy() {
    if (!this.isSend) this.attachments.forEach((attachment) => this.deleteAttachment(attachment));
    else this.ATTACHMENTS_CLEAR();
  },
  computed: {
    ...mapState('departments', ['departments', 'attachments']),
    ...mapGetters('activities', ['getActivityById']),
    sizeAttachment() {
      return this.$store.state.app[C_ATTACHMENT_MAX_SIZE];
    },
    currentActivity() {
      return this.getActivityById(this.value.activityId);
    },
    countOfFiles() {
      const currentChannelType = this.currentActivity.channelType;
      return this.$store.state.app[C_ATTACHMENT_MAX_COUNT][currentChannelType];
    },
    moreThanOneFile() {
      return this.countOfFiles > 1;
    },
    currentCountOfFiles() {
      return this.attachments ? this.attachments.length : 0;
    },
    fullFileStack() {
      return this.currentCountOfFiles >= this.countOfFiles;
    },
    allSended() {
      return !isEmpty(this.attachments)
        ? this.attachments.map((item) => item.send)
          .reduce((accumulator, currentValue) => accumulator && currentValue) : true;
    },
  },
  watch: {
    emails(value) {
      if (!value) this.errors.validate = this.$t('T_EMAIL_ERROR');
      else {
        this.errors.email = '';
        // проверка списка emails на валидность и для input и для select
        const emailsArray = !Array.isArray(value)
          ? value.split(/\s*,\s*/).filter((item) => item !== '')
          : value;
        const valid = emailsArray.every((item) => validateEmail(item));
        this.errorNotification = true;
        this.errors.validate = valid ? '' : this.$t('T_EMAIL_MUST_BE_VALID');
      }
    },
    message(value) {
      if (value) this.errors.message = '';
    },
  },
  methods: {
    ...mapActions('departments', ['fetchDepartments']),
    ...mapMutations('departments', ['ATTACHMENTS_CLEAR']),
    ...mapActions('operatorChat', [
      'sendToDepartment',
      'sendFileToDepartmentController',
      'removeFileFromDepartmentController',
      'resendFileToDepartmentController',
    ]),
    sendModalToDepartment() {
      if (isEmpty(this.emails)) this.errors.email = this.$t('T_EMAIL_ERROR');
      if (isEmpty(this.message)) this.errors.message = this.$t('T_MESSAGE_ERROR');
      // isErrors это список не пустых ошибок
      const isErrors = Object.keys(this.errors).filter((key) => this.errors[key] !== '');

      if (isEmpty(isErrors)) {
        const emails = !Array.isArray(this.emails) // проверка и преобразование emails в массив
          ? this.emails.split(/\s*,\s*/).filter((item) => item !== '').map((item) => item.toLowerCase())
          : this.emails.map((item) => item.toLowerCase());
        this.sendToDepartment({
          activityId: this.value.activityId,
          body: this.message,
          to: emails,
          attachments: this.attachments,
        })
          .then(() => {
            this.errorNotification = false;
            this.isSend = true;
            this.close();
          });
      } else {
        this.errorNotification = true;
      }
    },
    close() {
      this.$parent.close([null]);
    },
    resolve(answer) {
      if (answer) {
        this.sendModalToDepartment();
      } else this.close();
    },
    convertEmails(arrayEmails) {
      if (arrayEmails) {
        const preliminaryArr = arrayEmails.map((email) => `${email}`);
        return orderBy(uniqBy(preliminaryArr)).join(', ');
      }
      return '';
    },
    sendFile(event) {
      const { files } = event.target;
      if (files.length) {
        this.sendFileToDepartmentController({
          activityId: this.value.activityId,
          files,
        }).then(() => { this.$refs.fileInput.value = ''; });
      }
    },
    resendFile(temporaryId) {
      if (temporaryId) {
        this.resendFileToDepartmentController({
          activityId: this.value.activityId,
          temporaryId,
        });
      }
    },
    shortTitle(title) {
      const indexOfDot = Math.max(0, title.lastIndexOf('.'));
      return title.slice(0, indexOfDot < 50 ? indexOfDot : 50)
        .concat(indexOfDot < 50 ? '' : '..', title.slice((Math.max(0, title.lastIndexOf('.')) || Infinity)));
    },
    progress(attachment) {
      const { progress } = attachment || 0;
      return (progress * 100).toFixed(0);
    },
    longLength(attachment) {
      if (attachment.ext.length > 3) {
        return true;
      } return false;
    },
    openFile(attachment) {
      if (attachment.original && attachment.title !== 'attachment error') {
        window.open(this.$options.filters.fullpath(attachment.original), '_blank');
      } else {
        toast.error({
          title: this.$t('T_ERROR'),
          msg: this.$t('T_FILE_ERROR'),
          duration: consts.TOAST_NOTIFICATION_DURATION,
        });
      }
    },
    deleteAttachment({ temporaryId }) {
      if (temporaryId) {
        this.removeFileFromDepartmentController({
          activityId: this.value.activityId,
          temporaryId,
        });
      }
    },
    onClickFile(event) {
      if (this.fullFileStack) {
        event.preventDefault();
        toast.error({
          title: this.$t('T_FILE_UPLOADING_ERROR'),
          msg: this.$t('T_ALLOWED_FILE_COUNT_IS', [this.countOfFiles]),
          duration: consts.TOAST_NOTIFICATION_DURATION,
        });
      }
    },
  },
};
</script>

<template lang="pug">
div(:class="$style.root")
  header(:class="$style.header")
    div(:class="$style.headerTitle") {{ header }}
  div(:class="$style.content")
    div(:class="[$style.notificationSmall, !errorNotification && !errors.email && $style.notificationHidden]")
      | {{ errors.validate }} {{ errors.email }}
    div(:class="$style.contentNote") {{ $t('T_EMAIL_FROM_KEYBOARD') }}
    ui-input(
      :placeholder="$t('T_EMAIL_DEPARTMENT')",
      :value.sync="emails",
      :disabled="disabled",
      :class="$style.input",
    )
    div(:class="$style.contentNote") {{ $t('T_EMAIL_FROM_LIST') }}
    v-select(
      hide-details,
      :label="$t('T_EMAIL_DEPARTMENT')",
      v-model="emails",
      :items="departments",
      item-text="name",
      item-value="emails",
      :menu-props="{ zIndex: 10009 }",
      :class="$style.select",
      :no-data-text="$t('T_NO_AVAILABLE_EMAILS')")
      div(slot="item", slot-scope="data", :class="$style.selector")
        div(:class="$style.selectorName") {{ data.item.name }}
        div(:class="$style.selectorEmails") {{ convertEmails(data.item.emails) }}
  div(:class="$style.message")
    div(:class="[$style.notificationSmall, !errorNotification && !errors.message && $style.notificationHidden]")
      | {{ errors.message }}
    ui-input-text(
      required,
      :placeholder="$t('T_ENTER_TEXT_HERE')",
      :value.sync="message",
      :disabled="disabled",
      :class="$style.messageInput",
      :minheight="160")
  div(:class="$style.file")
    div(:class="[$style.notificationSmall, !errorNotification && !errors.file && $style.notificationHidden]")
      | {{ errors.file }}
    div(:class="$style.fileContent")
      input(v-if="moreThanOneFile", multiple, type="file",
        @click="onClickFile($event)",:class="$style.fileInput", @change="sendFile($event)", ref="fileInput")
      input(v-else, type="file", :class="$style.fileInput",
        @click="onClickFile($event)", @change="sendFile($event)", ref="fileInput")
      ui-icon(:class="$style.fileIco") attach

      div(:class="$style.attachments", v-if="attachments")
        div(:class="$style.attachment", v-for="attachment in attachments")
          ui-icon(:class="$style.delete", key="delete",
            :title="$t('T_DELETE')", @click.native="deleteAttachment(attachment)") delete
          div(:class="$style.preview", v-if="attachment.type === 'image'", @click="openFile(attachment)",
            :style="{backgroundImage: `url(${$options.filters.fullpath(attachment.preview)})`}")
          div(:class="$style.file", @click="openFile(attachment)", v-else)
            span(:class="[$style.fileExt, longLength(attachment) && $style.fileExtLong]")
              | {{ (attachment.ext).replace('.', '') }}
            ui-icon(:class="$style.ico") file-extension
          div(:class="$style.info", @click="openFile(attachment)")
            div(:class="$style.title", :title="attachment.title") {{ shortTitle(attachment.title) }}
            div(:class="$style.subtitle")
              div(:class="$style.ext") {{ attachment.ext }}
              div(:class="$style.size") {{ attachment.size | humanFileSize }}
            v-progress-linear(:class="$style.progress", v-if="attachment.send === undefined"
              :value="progress(attachment)" height="2" color="secondary")
          ui-icon(:class="[$style.status, $style.green]", key="done",
            v-if="attachment.send", :title="$t('T_SENT')") done
          ui-icon(:class="[$style.status, $style.red]", key="cached",
            v-if="attachment.send === false", :title="$t('T_RESEND')",
            @click.native="resendFile(attachment.temporaryId)") cached

  div(:class="$style.action", date-test="REPLY_BUTTON")
    ui-button(colored="green", @click.native="resolve(true)", :disabled="!allSended") {{ $t('T_SEND') }}
    ui-button(colored="red", @click.native="resolve(false)") {{ $t('T_CANCEL') }}
</template>

<style lang="sass" module>
.root
  display: block
  max-width: 1000px
.header
  padding: 24px
  border-bottom: 1px solid $color_divider
  &Title
    font-size: 18px
    display: flex
    justify-content: center
.input
  margin: 18px 0
  input
    font-weight: normal
.select
  label
    font-size: 14px
.content
  display: flex
  flex-direction: column
  padding: 24px 24px 4px
  min-width: 700px
  position: relative
  &Note
    color: $color_primary
    padding: 5px 0
.action
  display: flex
  padding: 24px
  justify-content: space-around
  padding-top: 40px
.messageInput
  margin-top: 10px
.selector
  display: flex
  justify-content: space-between
  width: 100%
  &Name
    display: flex
    align-items: center
    max-width: 500px
  &Emails
    display: flex
    align-items: center
    max-width: 500px
    padding-left: 30px
    text-align: right
.notification
  display: flex
  padding: 8px
  justify-content: center
  color: $color_red
  font-size: 18px
  height: 44px
  &Hidden
    opacity: 0
  &Small
    color: $color_red
    font-size: 12px
    height: 14px
    line-height: 14px
.message
  flex-grow: 1
  margin: 0 24px
  position: relative
  &Input
    padding-top: 18px
    border-top: 1px solid rgba(0, 0, 0, 0.12)
    textarea
      font-weight: normal
.file
  color: $color_text_second
  overflow: hidden
  position: relative
  z-index: 1
  padding: 0 0 16px 0
  margin: 15px 24px 0
  border-bottom: 1px solid $color_divider
  line-height: 24px
  display: flex
  flex-direction: column
  flex-wrap: nowrap
  &Content
    padding-top: 18px
    display: flex
    align-items: center
  &Ico
    width: 24px
    height: 24px
    z-index: 10
    cursor: pointer
  &Input
    position: absolute
    z-index: 20
    height: 24px
    width: 24px
    opacity: 0
    &:hover
      + .fileIco
        color: $color_text
.progress
  height: 2px
  width: 100%
  position: absolute
  bottom: 0
.attachments
  display: flex
  flex-basis: 100%
  flex-wrap: wrap
  margin-top: 12px
  .attachment
    display: flex
    position: relative
    align-items: center
    justify-content: flex-start
    overflow: hidden
    cursor: pointer
    margin-right: 12px
    margin-bottom: 12px
    .delete
      position: absolute
      z-index: 10
      top: 3px
      right: 0px
      width: 16px
      height: 16px
      color: $color_red
      &:hover
        opacity: 0.5
    .status
      position: absolute
      bottom: 3px
      right: 0px
      width: 16px
      height: 16px
      &.green
        color: $color_green
      &.red
        color: $color_red
  .preview
    background: $color_bg_light
    border: 4px solid $color_bg_light
    border-radius: 2px
    flex-grow: 0
    flex-shrink: 0
    height: 80px
    width: 80px
    background-size: cover
  .file
    height: 60px
    position: relative
    flex-grow: 0
    flex-shrink: 0
    background: $color_bg_light
    padding: 12px 0 12px 8px
    border-radius: 10px 0 0 10px
    z-index: 1
    &Ext
      z-index: 20
      position: absolute
      color: $white
      font-weight: 500
      width: 28px
      text-align: center
      top: 17px
      left: 14px
      font-size: 13px
      transform: scaleX(0.8)
      text-transform: uppercase
      &Long
        font-size: 10px
  .info
    line-height: 18px
    padding: 12px 16px 12px 8px
    border-radius: 0 10px 10px 0
    overflow: hidden
    position: relative
    background: $color_bg_light
  .ico
    color: $color_text_second
    position: relative
    z-index: 10
    height: 34px
    width: 34px
  .title
    color: $color_text
    font-weight: 400
    font-size: 12px
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
  .subtitle
    font-size: 10px
    color: $color_text_second
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
    display: flex
    justify-content: space-between
  .ext
    margin-right: 4px
  .progress
    position: absolute
    right: 16px
    left: 8px
    width: auto !important

</style>
